// Implementation plan and execution model (Screens 7 and 8).
//
// The plan is part of the ONE connected Change record. Screen 7 builds and freezes
// the execution plan from the approved assessment; Screen 8 monitors execution of
// the same tasks. Document, regulatory, training and external activities all use
// this single task model - they are never duplicated into separate systems.

import type { TSparseItem } from '@modules/items';

// -- Task types (prompt Part 7). Document revision, validation, training and
// regulatory activities are task types, not separate records.
export const TASK_TYPE = {
  GENERAL: 'General',
  DOCUMENT_REVISION: 'Document revision',
  SUPPLIER_QUALIFICATION: 'Supplier qualification',
  RISK_MANAGEMENT: 'Risk management',
  BIOLOGICAL_EVALUATION: 'Biological evaluation',
  VERIFICATION_VALIDATION: 'Verification or validation',
  REGULATORY_ASSESSMENT: 'Regulatory assessment',
  EXTERNAL_DECISION: 'External decision',
  TRAINING_REQUIREMENT: 'Training requirement',
  PRODUCTION_CUTOVER: 'Production cutover',
  CAPA_ACTION: 'CAPA action',
} as const;
export type TTaskType = (typeof TASK_TYPE)[keyof typeof TASK_TYPE];

// -- Execution statuses (prompt Part 8). Clear, small and mutually exclusive.
export const TASK_STATUS = {
  NOT_STARTED: 'Not started',
  IN_PROGRESS: 'In progress',
  IN_REVIEW: 'In review',
  COMPLETE: 'Complete',
} as const;
export type TTaskStatus = (typeof TASK_STATUS)[keyof typeof TASK_STATUS];

export const TASK_PRIORITY = {
  LOW: 'Low',
  NORMAL: 'Normal',
  HIGH: 'High',
} as const;
export type TTaskPriority = (typeof TASK_PRIORITY)[keyof typeof TASK_PRIORITY];

// A decision made by an external notified body or authority. It is an external
// gate, never an internal approval, so it carries its own status vocabulary.
export const EXTERNAL_DECISION_STATUS = {
  NOT_STARTED: 'Not started',
  ASSESSMENT_PENDING: 'Assessment pending',
  DECISION_RECEIVED: 'Decision received',
} as const;
export type TExternalDecisionStatus = (typeof EXTERNAL_DECISION_STATUS)[keyof typeof EXTERNAL_DECISION_STATUS];

// A single plan/execution task. One shape covers planning (Screen 7) and execution
// (Screen 8); execution-only fields stay undefined until work starts.
export type TPlanTask = {
  id: string;
  title: string;
  description?: string;
  workPackageId: string;
  taskType: TTaskType;

  // Ownership and scheduling
  owner?: string;
  responsibleFunction?: string;
  contributors: string[];
  status: TTaskStatus;
  priority: TTaskPriority;
  startDate?: string;
  dueDate?: string;

  // Dependencies, completion definition and controlled links
  dependencies: string[]; // ids of tasks that must complete first
  linkedObject?: string; // e.g. a document number or CAPA reference
  documents?: TSparseItem[];
  implementationProposal?: import('../../change/types').ImplementationProposal;
  proposalAcceptedAt?: string;
  completionCriteria?: string;

  // Whether the task is mandatory for the plan to be ready / release to proceed.
  required: boolean;
  // Marks an external notified-body / authority activity (external gate).
  external?: boolean;
  externalStatus?: TExternalDecisionStatus;
  // An external activity that blocks release until the decision is resolved.
  releaseBlocking?: boolean;

  // Origin / traceability
  origin: 'Assessment' | 'Automation' | 'Manual' | 'CAPA';
  sourceActivityId?: string;
  automationRule?: string;

  // Execution state
  completedBy?: string;
  completedAt?: string;
  deviations?: string;
};

export type TWorkPackage = {
  id: string;
  name: string;
  responsibleFunction: string;
  order: number;
};

// The frozen plan baseline created when execution starts (Plan version 1).
export type TPlanBaseline = {
  version: number;
  frozenAt: string;
  frozenBy: string;
  taskCount: number;
  workPackageCount: number;
  targetDate?: string;
};

// A newly discovered impact reported during execution (prompt Part 8). A material
// impact is never decided silently - it triggers a visible reassessment.
export type TReportedImpact = {
  id: string;
  description: string;
  affectedObject: string;
  materialScopeChange: 'Yes' | 'No' | 'Unsure';
  suggestedFollowUp?: string;
  reportedBy: string;
  reportedAt: string;
  material: boolean;
};

// The plan record attached to a Change from Screen 7 onward.
export type TChangePlan = {
  workPackages: TWorkPackage[];
  tasks: TPlanTask[];
  targetDate?: string;
  baseline?: TPlanBaseline;
  reportedImpacts: TReportedImpact[];
  reassessmentRequired: boolean;
};

export const TASK_TYPE_OPTIONS: TTaskType[] = Object.values(TASK_TYPE);
export const TASK_STATUS_OPTIONS: TTaskStatus[] = Object.values(TASK_STATUS);
export const TASK_PRIORITY_OPTIONS: TTaskPriority[] = Object.values(TASK_PRIORITY);
